/**
 * Where profile setup opens (docs/screens.md §Profile setup):
 *
 * - `ProfileSetupPrompt` (mounted on Tonight): once per account per device,
 *   only when the profile has gaps, after `['myProfile']` has resolved (so a
 *   `404 needs_setup` bootstrap is never mistaken for a profile). Marked shown
 *   the moment it opens; a complete profile is marked too. Port of the app's
 *   `ProfileSetupPrompt.tsx`, without its `onboarding_v1` flag (that flag
 *   targets native app versions the web cannot meet).
 * - `ReplayIntroCard` (`/settings` → General, a plain settings row): opens it again right away, with
 *   the language step plus whatever is still missing.
 *
 * The wizard itself is a lazy chunk, so neither screen waits on it.
 */
import { lazy, Suspense, useCallback, useContext, useEffect, useState } from 'react';
import { PlayCircle } from 'lucide-react';

import { AuthContext } from '@/auth/authContext';
import { Button } from '@/components/ui/button';
import { useMyProfile } from '@/features/profile/api';
import { SettingRow } from '@/features/profile/SettingRow';
import { useLocale } from '@/i18n/locale';

import {
  markProfileSetupShown,
  profileGaps,
  setupSteps,
  wasProfileSetupShown,
  type SetupStep,
} from './profile-setup';

const ProfileSetupDialog = lazy(() =>
  import('./ProfileSetupDialog').then((m) => ({ default: m.ProfileSetupDialog })),
);

/** The Cognito sub, or undefined (signed out, or rendered without an AuthProvider). */
function useAccountSub(): string | undefined {
  const auth = useContext(AuthContext);
  return auth?.user?.sub ?? auth?.session?.sub ?? undefined;
}

/** Open state for the lazy wizard; `start` marks it shown for this account. */
function useProfileSetup(sub: string | undefined) {
  const [run, setRun] = useState<{ id: number; steps: SetupStep[] } | null>(null);
  const [open, setOpen] = useState(false);

  const start = useCallback(
    (steps: SetupStep[]) => {
      if (sub) markProfileSetupShown(sub);
      setRun((prev) => ({ id: (prev?.id ?? 0) + 1, steps }));
      setOpen(true);
    },
    [sub],
  );

  const element = run && (
    <Suspense fallback={null}>
      {/* A fresh key per run resets the wizard to its first step. */}
      <ProfileSetupDialog key={run.id} steps={run.steps} open={open} onOpenChange={setOpen} />
    </Suspense>
  );
  return { start, element };
}

export function ProfileSetupPrompt() {
  const sub = useAccountSub();
  const profile = useMyProfile().data;
  const { start, element } = useProfileSetup(sub);

  useEffect(() => {
    if (!sub || !profile || wasProfileSetupShown(sub)) return;
    if (profileGaps(profile).length > 0) start(setupSteps(profile));
    else markProfileSetupShown(sub);
  }, [sub, profile, start]);

  return element;
}

export function ReplayIntroCard() {
  const { t } = useLocale();
  const sub = useAccountSub();
  const profile = useMyProfile().data;
  const { start, element } = useProfileSetup(sub);

  return (
    <SettingRow
      title={t('setup.replay.title')}
      description={t('setup.replay.hint')}
      control={
        <Button variant="neutral" disabled={!profile} onClick={() => profile && start(setupSteps(profile))}>
          <PlayCircle className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          {t('setup.replay.action')}
        </Button>
      }
    >
      {element}
    </SettingRow>
  );
}
